import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History } from "lucide-react";
import { api, DOC_LABELS, money } from "@/lib/api";
import { formatPhone } from "@/lib/phone";

export default function CustomerHistory({ query, onPick }) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 3) {
      setMatches([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .get("/customers/lookup", { params: { q } })
        .then(({ data }) => {
          setMatches(data);
          setOpen(true);
        })
        .catch(() => setMatches([]));
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  if (!matches.length || !open) return null;

  return (
    <div className="border border-[#C5A059] bg-[#FBF8F2]" data-testid="customer-history-panel">
      <div className="flex items-center justify-between border-b border-[#E6D9BD] px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-bold">
          <History className="h-4 w-4 text-[#B38F4E]" />
          Returning customer found
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="dismiss-customer-history"
        >
          Dismiss
        </button>
      </div>
      <div className="divide-y divide-[#E6D9BD]">
        {matches.map((m, i) => (
          <div key={i} className="px-4 py-3" data-testid={`customer-match-${i}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Current name
                </span>
                <div>
                  <span className="text-sm font-semibold">{m.customer_name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {formatPhone(m.customer_phone)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onPick(m)}
                className="bg-[#0A0A0A] px-3 py-1 text-xs font-semibold text-white transition-colors duration-150 hover:bg-[#242424]"
                data-testid={`use-customer-${i}`}
              >
                Use these details
              </button>
            </div>
            {m.names?.length > 1 && (
              <div className="mt-2" data-testid={`other-names-${i}`}>
                <span className="text-xs text-muted-foreground">
                  Also invoiced as — tap to use that name instead:
                </span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {m.names.slice(1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => onPick({ ...m, customer_name: n })}
                      className="border border-[#C5A059] bg-white px-2 py-1 text-[11px] font-medium transition-colors duration-150 hover:bg-[#F6EEDD]"
                      data-testid={`use-name-${i}-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-1.5 text-xs text-muted-foreground">
              {m.purchase_count} purchase(s) · {money(m.total_spent)} lifetime
              {m.active_loans > 0 && (
                <span className="ml-1 font-semibold text-[#9A3412]">
                  · {m.active_loans} active loan(s)
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {m.documents.map((d) => (
                <Link
                  key={d.id}
                  to={`/documents/${d.id}`}
                  className="border border-[#E6D9BD] bg-white px-2 py-1 text-[11px] transition-colors duration-150 hover:border-[#C5A059]"
                  data-testid={`history-doc-${d.doc_number}`}
                >
                  {DOC_LABELS[d.doc_type]} #{d.doc_number} · {money(d.total)}
                  {d.loan_status ? ` · ${d.loan_status}` : ""}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
